/**
 * KnowledgeMapView — a glanceable map of the fold, split into two groups:
 *
 *  - Clinical concepts  → skills carrying a terminology coding (searchable)
 *  - Further skills     → skills without one (free labels, still valid)
 *
 * Real concept clustering arrives in a later phase; this is the honest
 * two-bucket split — no fabricated topology. Read-only chips; the nurse acts on
 * skills in the list view.
 *
 * It maps the HELD skills only. A proposal is a candidate, not a skill she
 * holds, and drawing it on the map would put the curator's guess on her profile
 * before she accepted it. A rejected one is not hers either.
 *
 * DESIGN.md: flat hairline chips, citation tint for coded concepts (the
 * search-strength signal), neutral tint for the rest. No shadow, no gradient.
 */

import { useTranslation } from 'react-i18next'
import type { HeldSkill } from '@frontend/api/endpoints/skills'
import { heldSkillTitle } from '@/lib/skillTitle'

interface KnowledgeMapViewProps {
  skills: HeldSkill[]
}

function Chip({ skill }: { skill: HeldSkill }) {
  const coded = skill.coding !== null
  const className = coded
    ? 'bg-[var(--color-citation-50)] text-[var(--color-citation-700)]'
    : 'bg-[var(--color-tone-mute-tint)] text-[var(--color-surface-ink)]'
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1.5 text-[15px] font-medium ${className}`}
      title={skill.coding ? `${skill.coding.system}:${skill.coding.code}` : undefined}
    >
      {heldSkillTitle(skill)}
    </span>
  )
}

function Group({ id, title, skills }: { id: string; title: string; skills: HeldSkill[] }) {
  const { t } = useTranslation()
  return (
    <section aria-labelledby={id}>
      <h2 id={id} className="mb-3 text-[15px] font-semibold text-[var(--color-surface-mute)]">
        {title}
      </h2>
      {skills.length === 0 ? (
        <p className="text-[15px] text-[var(--color-surface-mute)]">{t('skills.map.empty')}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {skills.map((skill) => (
            <Chip key={skill.key} skill={skill} />
          ))}
        </div>
      )}
    </section>
  )
}

export function KnowledgeMapView({ skills }: KnowledgeMapViewProps) {
  const { t } = useTranslation()
  const held = skills.filter((s) => s.state === 'held')
  const concepts = held.filter((s) => s.coding !== null)
  const further = held.filter((s) => s.coding === null)

  return (
    <div className="space-y-8">
      <Group
        id="map-clinical-concepts"
        title={t('skills.map.clinicalConcepts')}
        skills={concepts}
      />
      <Group id="map-further-skills" title={t('skills.map.furtherSkills')} skills={further} />
    </div>
  )
}

export default KnowledgeMapView
